import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getTaxInvoices, getTaxInvoiceBuyers, getTaxInvoiceSellers, getDefaultTaxInvoiceSeller } from '../../utilities/api';

export const fetchTaxInvoices = createAsyncThunk('taxInvoice/fetchInvoices', async (params = {}, { rejectWithValue }) => {
    try {
        const data = await getTaxInvoices(params);
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch tax invoices');
    }
});

export const fetchTaxInvoiceBuyers = createAsyncThunk('taxInvoice/fetchBuyers', async (_, { rejectWithValue }) => {
    try {
        const data = await getTaxInvoiceBuyers();
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch buyers');
    }
});

export const fetchTaxInvoiceSellers = createAsyncThunk('taxInvoice/fetchSellers', async (_, { rejectWithValue }) => {
    try {
        const data = await getTaxInvoiceSellers();
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch sellers');
    }
});

export const fetchDefaultTaxInvoiceSeller = createAsyncThunk('taxInvoice/fetchDefaultSeller', async (_, { rejectWithValue }) => {
    try {
        const data = await getDefaultTaxInvoiceSeller();
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch default seller');
    }
});

const initialState = {
    invoices: [],
    buyers: [],
    sellers: [],
    defaultSeller: null,
    loading: false,
    error: null,
    statusFilter: 'all'
};

const taxInvoiceSlice = createSlice({
    name: 'taxInvoice',
    initialState,
    reducers: {
        setStatusFilter: (state, action) => {
            state.statusFilter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTaxInvoices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTaxInvoices.fulfilled, (state, action) => {
                state.loading = false;
                const raw = action.payload;
                state.invoices = Array.isArray(raw?.data) ? raw.data : [];
            })
            .addCase(fetchTaxInvoices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.invoices = [];
            })
            .addCase(fetchTaxInvoiceBuyers.fulfilled, (state, action) => {
                const raw = action.payload;
                state.buyers = Array.isArray(raw?.data) ? raw.data : [];
            })
            .addCase(fetchTaxInvoiceSellers.fulfilled, (state, action) => {
                const raw = action.payload;
                state.sellers = Array.isArray(raw?.data) ? raw.data : [];
            })
            .addCase(fetchDefaultTaxInvoiceSeller.fulfilled, (state, action) => {
                state.defaultSeller = action.payload?.data || null;
            });
    }
});

export const { setStatusFilter, clearError } = taxInvoiceSlice.actions;

export const selectInvoices = (state) => state.taxInvoice.invoices;
export const selectTaxInvoiceBuyers = (state) => state.taxInvoice.buyers;
export const selectTaxInvoiceSellers = (state) => state.taxInvoice.sellers;
export const selectDefaultSeller = (state) => state.taxInvoice.defaultSeller;
export const selectLoading = (state) => state.taxInvoice.loading;
export const selectError = (state) => state.taxInvoice.error;
export const selectStatusFilter = (state) => state.taxInvoice.statusFilter;

export const selectFilteredInvoices = (state) => {
    const { invoices, statusFilter } = state.taxInvoice;
    if (statusFilter === 'all') return invoices;
    return invoices.filter((inv) => inv.status === statusFilter);
};

export default taxInvoiceSlice.reducer;
